import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Settings2, Truck, Phone, Mail,
  CheckCircle2, Loader2, ShoppingBag, RotateCcw, ArrowUpDown, Wifi,
} from 'lucide-react';
import { invalidatePublicConfig } from '../../hooks/usePublicConfig';

// ── Types ────────────────────────────────────────────────────────────────────

interface SiteSettings {
  qty_rounding_direction: 'floor' | 'ceil';
  min_order_amount: number;
  support_email: string;
  support_phone: string;
  presence_window_minutes: number;
}


// ── Sub-components ────────────────────────────────────────────────────────────

function SectionCard({ icon: Icon, title, children }: { icon: React.ElementType; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border bg-white overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center gap-3">
        <Icon className="w-4 h-4 text-muted-foreground" />
        <h2 className="font-semibold">{title}</h2>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <div className="sm:w-56 shrink-0">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

export default function AdminConfigPage() {
  const [settings, setSettings] = useState<SiteSettings>({
    qty_rounding_direction: 'floor',
    min_order_amount: 0,
    support_email: '',
    support_phone: '',
    presence_window_minutes: 15,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get<SiteSettings>('/api/erp/admin/site-settings').then(r => {
      setSettings(r.data);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/erp/admin/site-settings', settings);
      invalidatePublicConfig();
      toast.success('Impostazioni sito salvate');
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof SiteSettings>(key: K, val: SiteSettings[K]) =>
    setSettings(s => ({ ...s, [key]: val }));

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Settings2 className="w-6 h-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Configurazione sito</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            Impostazioni globali dell'e-commerce. Le modifiche sono attive al successivo caricamento pagina.
          </p>
        </div>
      </div>

      {/* ── 1. Quantità & Vendite ─────────────────────────────────────── */}
      <SectionCard icon={ArrowUpDown} title="Quantità & Vendite">
        <FieldRow
          label="Arrotondamento minimo vendibile"
          hint="Come arrotondare quando l'utente inserisce manualmente una quantità non multipla del minimo vendibile"
        >
          <div className="flex gap-2" data-testid="qty-rounding-selector">
            {([
              { val: 'floor', label: 'Per difetto', desc: '5 → 4 (con min 2)' },
              { val: 'ceil', label: 'Per eccesso', desc: '5 → 6 (con min 2)' },
            ] as const).map(opt => (
              <button
                key={opt.val}
                onClick={() => set('qty_rounding_direction', opt.val)}
                data-testid={`qty-rounding-${opt.val}`}
                className={`flex-1 sm:flex-none px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  settings.qty_rounding_direction === opt.val
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'hover:bg-secondary/60'
                }`}
              >
                <div>{opt.label}</div>
                <div className="text-[10px] font-normal opacity-70 mt-0.5">{opt.desc}</div>
              </button>
            ))}
          </div>
        </FieldRow>

        <div className="border-t pt-4" />

        <FieldRow
          label="Importo minimo ordine (€)"
          hint="Il cliente non può concludere l'ordine se il totale è inferiore a questo valore. 0 = nessun minimo"
        >
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">€</span>
            <input
              type="number" min={0} step={0.01}
              value={settings.min_order_amount}
              onChange={e => set('min_order_amount', parseFloat(e.target.value) || 0)}
              className="w-32 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="min-order-amount-input"
            />
          </div>
        </FieldRow>

        <p className="text-[11px] text-muted-foreground pt-1">
          La soglia di spedizione gratuita non è un'impostazione globale: è un valore
          specifico per ciascun cliente, sincronizzato dal gestionale (visibile e
          gestito nella scheda cliente, sezione Logistica &amp; Spedizione).
        </p>

        <div className="pt-4 flex justify-end">
          <button
            onClick={save} disabled={saving || !loaded}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="site-settings-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Salva impostazioni
          </button>
        </div>
      </SectionCard>

      {/* ── 2b. Presenza clienti ─────────────────────────────────────── */}
      <SectionCard icon={Wifi} title="Presenza Clienti">
        <FieldRow
          label="Finestra online (minuti)"
          hint="Da quanti minuti di inattività un cliente non è più considerato online. Vale per la card 'Online ora' della dashboard e per il filtro omonimo in Clienti. Il portale non manda segnali periodici, quindi la finestra è il tempo fra due azioni reali: sotto i 10 minuti chi legge una pagina a lungo sparisce pur essendo presente."
        >
          <div className="flex items-center gap-2">
            <input
              type="number" min={1} max={1440} step={1}
              value={settings.presence_window_minutes}
              onChange={e => set('presence_window_minutes', parseInt(e.target.value, 10) || 1)}
              className="w-24 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="presence-window-input"
            />
            <span className="text-muted-foreground text-sm">minuti (default 15, max 1440)</span>
          </div>
        </FieldRow>
        <div className="pt-4 flex justify-end">
          <button
            onClick={save} disabled={saving || !loaded}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="presence-settings-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Salva finestra online
          </button>
        </div>
      </SectionCard>

      {/* ── 3. Contatti & Supporto ───────────────────────────────────── */}
      <SectionCard icon={Phone} title="Contatti & Supporto">
        <p className="text-sm text-muted-foreground">
          Riferimenti mostrati nelle pagine di contatto, nella mail di conferma ordine e nel footer del sito.
        </p>
        <FieldRow label="Email supporto">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="email"
              value={settings.support_email}
              onChange={e => set('support_email', e.target.value)}
              placeholder="es. supporto@dtk.it"
              className="w-64 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="support-email-input"
            />
          </div>
        </FieldRow>
        <FieldRow label="Telefono supporto">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              type="tel"
              value={settings.support_phone}
              onChange={e => set('support_phone', e.target.value)}
              placeholder="es. 02 1234567"
              className="w-64 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              data-testid="support-phone-input"
            />
          </div>
        </FieldRow>
        <div className="pt-4 flex justify-end">
          <button
            onClick={save} disabled={saving || !loaded}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="support-contacts-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Salva contatti
          </button>
        </div>
      </SectionCard>

      {/* ── 5. Spedizione & Logistica ─────────────────────────────────── */}
      <SectionCard icon={Truck} title="Spedizione & Logistica">
        <div className="flex gap-3 flex-wrap">
          <a
            href="/admin/ordini"
            className="flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium hover:bg-secondary/60 transition-colors"
          >
            <ShoppingBag className="w-4 h-4" />
            Gestisci ordini
          </a>
        </div>
      </SectionCard>

      {/* ── 6. Aggiornamenti dal gestionale ─────────────────────────── */}
      <SectionCard icon={RotateCcw} title="Aggiornamenti dal gestionale">
        <p className="text-sm text-muted-foreground">
          Frequenza, esecuzioni e storico degli aggiornamenti da NTS Business (articoli, clienti, prezzi, agenti…).
        </p>
        <a
          href="/admin/sync-erp"
          className="inline-flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium hover:bg-secondary/60 transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Vai agli Aggiornamenti dal gestionale
        </a>
      </SectionCard>
    </div>
  );
}
